import {
    getUtf8ByteLength,
    normalizeSelectionRange,
    utf8OffsetToUtf16Index,
    utf16IndexToUtf8Offset,
    type TextSelectionRange,
} from '@/utils/text-kit';
import type { Optional } from '@/version-types';
import { type EditorTextSelection } from '../types';

/**
 * 将文本选区限制在当前文本内容范围内。
 *
 * 会先规范化选区方向，再用文本的 UTF-8 字节长度裁剪 `start` 和 `end`，
 * 避免文本变短、撤销重做或外部传入范围时出现越界选区。
 *
 * @param text - 提供文本内容的文本。
 * @param range - 需要裁剪的文本选区范围。
 * @returns 裁剪后的文本选区范围。
 */
const clampTextSelectionRange = (text: string, range: TextSelectionRange): TextSelectionRange => {
    const length = getUtf8ByteLength(text);
    const normalized = normalizeSelectionRange(range);
    return {
        start: Math.min(normalized.start, length),
        end: Math.min(normalized.end, length),
    };
};

/**
 * 构造编辑器内部选区。
 *
 * 外部传入的普通选区只包含 `start` 和 `end`，这里会补齐 `anchor` 和 `focus`，
 * 用于保留反向选择、Shift 扩展选择和拖拽选择的方向信息。同时会按图层文本长度裁剪范围。
 *
 * @param text - 当前编辑的文本内容。
 * @param range - 可选的目标选区；未传时默认使用文本开头的折叠选区。
 * @returns 带方向信息的编辑器内部选区。
 */
export const makeEditorTextSelection = (
    text: string,
    range?: Optional<TextSelectionRange | EditorTextSelection>,
): EditorTextSelection => {
    const source = (range ?? { start: 0, end: 0 }) as TextSelectionRange &
        Partial<Pick<EditorTextSelection, 'anchor' | 'focus'>>;

    const anchor = source.anchor;
    const focus = source.focus;
    const hasDirection = typeof anchor === 'number' && typeof focus === 'number';
    const directional = hasDirection ? { start: anchor, end: focus } : { start: source.start, end: source.end };

    const normalized = clampTextSelectionRange(text, directional);
    const backward = hasDirection && anchor > focus;

    return {
        ...normalized,
        anchor: backward ? normalized.end : normalized.start,
        focus: backward ? normalized.start : normalized.end,
    };
};

/**
 * 将 textarea 原生选区转换为文本编辑器选区。
 *
 * textarea 使用 UTF-16 字符索引，文本编辑器使用 UTF-8 字节偏移；这里负责完成偏移转换，
 * 并根据 textarea 的 `selectionDirection` 写入 `anchor` 和 `focus`。
 *
 * @param text - textarea 当前文本。
 * @param selectionStart - textarea 原生选区起点。
 * @param selectionEnd - textarea 原生选区终点。
 * @param direction - textarea 原生选区方向。
 * @returns 带方向信息的编辑器内部选区。
 */
export const textareaSelectionToTextEditorSelection = (
    text: string,
    selectionStart: number,
    selectionEnd: number,
    direction?: 'forward' | 'backward' | 'none',
): EditorTextSelection => {
    const range = normalizeSelectionRange({
        start: utf16IndexToUtf8Offset(text, selectionStart),
        end: utf16IndexToUtf8Offset(text, selectionEnd),
    });
    const backward = direction === 'backward';

    return {
        ...range,
        anchor: backward ? range.end : range.start,
        focus: backward ? range.start : range.end,
    };
};

/**
 * 将文本编辑器选区转换为 textarea 原生选区。
 *
 * 文本编辑器使用 UTF-8 字节偏移，textarea 需要 UTF-16 字符索引；这里负责完成偏移转换，
 * 并在内部选区带有 `anchor` 和 `focus` 时恢复原生选区方向。
 *
 * @param text - textarea 当前文本。
 * @param range - 文本编辑器选区。
 * @returns textarea 可直接使用的选区起止位置和方向。
 */
export const textEditorSelectionToTextareaSelection = (
    text: string,
    range: TextSelectionRange | EditorTextSelection,
) => {
    const normalized = normalizeSelectionRange(range);

    return {
        start: utf8OffsetToUtf16Index(text, normalized.start),
        end: utf8OffsetToUtf16Index(text, normalized.end),
        direction: 'anchor' in range && range.anchor > range.focus ? ('backward' as const) : ('forward' as const),
    };
};

type SegmenterCtor = new (
    locale: string | undefined,
    options: { granularity: 'word' },
) => { segment(text: string): Iterable<{ index: number; segment: string; isWordLike?: boolean }> };

/**
 * 获取指定偏移所在的词选区。
 *
 * 优先使用 `Intl.Segmenter` 按词粒度分词，能更好处理多语言文本；环境不支持时，
 * 退回到基于非空白字符的简单匹配。若偏移落在非词内容上，则返回当前位置的折叠选区。
 *
 * @param text - 当前文本内容。
 * @param offset - UTF-8 字节偏移。
 * @returns 偏移所在词的选区范围。
 */
export const getWordRange = (text: string, offset: number): TextSelectionRange => {
    const textLength = getUtf8ByteLength(text);
    if (textLength === 0) return { start: 0, end: 0 };

    const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterCtor }).Segmenter;
    const segments = Segmenter ? Array.from(new Segmenter(undefined, { granularity: 'word' }).segment(text)) : null;

    if (segments) {
        for (const segment of segments) {
            const start = utf16IndexToUtf8Offset(text, segment.index);
            const end = utf16IndexToUtf8Offset(text, segment.index + segment.segment.length);
            if (offset >= start && offset < end) {
                return segment.isWordLike ? { start, end } : { start: offset, end: offset };
            }
        }
        return { start: offset, end: offset };
    }

    const index = utf8OffsetToUtf16Index(text, offset);
    const left = text.slice(0, index).search(/\S+$/);
    const rightMatch = text.slice(index).match(/^\S+/);
    const startIndex = left < 0 ? index : left;
    const endIndex = index + (rightMatch?.[0].length ?? 0);

    return {
        start: utf16IndexToUtf8Offset(text, startIndex),
        end: utf16IndexToUtf8Offset(text, endIndex),
    };
};

/**
 * 获取指定偏移所在的逻辑行选区。
 *
 * 逻辑行以换行符 `\n` 分隔，不依赖排版后的视觉换行。常用于三击选择当前文本行。
 *
 * @param text - 当前文本内容。
 * @param offset - UTF-8 字节偏移。
 * @returns 偏移所在逻辑行的选区范围。
 */
export const getLogicalLineRange = (text: string, offset: number): TextSelectionRange => {
    const index = utf8OffsetToUtf16Index(text, offset);
    const startIndex = text.lastIndexOf('\n', Math.max(0, index - 1)) + 1;
    const nextBreak = text.indexOf('\n', index);
    const endIndex = nextBreak === -1 ? text.length : nextBreak;

    return {
        start: utf16IndexToUtf8Offset(text, startIndex),
        end: utf16IndexToUtf8Offset(text, endIndex),
    };
};
